/**
 *
 * @param date ISO-8601 format, e.g. 2000-01-01T12:00:00+02:00
 */
export const formatDateString = (
  date?: string | undefined,
  style: "short" | "long" = "short",
  includeTime: boolean = false,
): string => {
  if (date == null) return "";
  const d = new Date(date);

  if (d.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${date}`);
  }

  return formatDate(d, style, includeTime);
};

export const formatDate = (
  date: Date,
  style: "short" | "long" = "short",
  includeTime: boolean = false,
): string => {
  if (date.toString() === "Invalid Date") {
    throw new Error(`Date is invalid`);
  }

  const options: Intl.DateTimeFormatOptions = {
    dateStyle: style,
  };

  if (includeTime) {
    options.timeStyle = "short";
  }

  const intl = new Intl.DateTimeFormat(["nb-NO", "en-GB"], options);

  return intl.format(date).replace(",", " kl.");
};

/**
 * Returns true if date is in the past according to users browser time
 * @param date ISO-8601 format, e.g. 2000-01-01T12:00:00+02:00
 */
export const dateHasPassed = (date: string): boolean => {
  const d = new Date(date);

  if (d.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${date}`);
  }
  const now = new Date();

  return now > d;
};

/**
 *
 * @param from ISO-8601 format, e.g. 2000-01-01T12:00:00+02:00
 * @param to ISO-8601 format, e.g. 2000-01-01T12:00:00+02:00
 * @param fromInclusive If false, subtract one day. Defaults to true
 * @param toInclusive If false, subtract one day. Defaults to false
 * @param style Defaults to "short"
 * @returns
 */
export const formatDateStringInterval = (
  from?: string | undefined,
  to?: string | undefined,
  fromInclusive: boolean = true,
  toInclusive: boolean = false,
  style: "short" | "long" = "short",
): string => {
  if (from == null || to == null) return "";

  const fromDate = new Date(from);
  const toDate = new Date(to);

  if (fromDate.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${from}`);
  }

  if (toDate.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${to}`);
  }

  return formatDateInterval(
    fromDate,
    toDate,
    fromInclusive,
    toInclusive,
    style,
  );
};

export const formatDateInterval = (
  from: Date,
  to: Date,
  fromInclusive: boolean = true,
  toInclusive: boolean = false,
  style: "short" | "long" = "short",
): string => {
  if (from.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${from}`);
  }

  if (to.toString() === "Invalid Date") {
    throw new Error(`Could not parse date string ${to}`);
  }

  // New up dates to prevent mutating input
  var fromDate = new Date(from);
  var toDate = new Date(to);

  if (fromInclusive !== true) {
    fromDate.setDate(fromDate.getDate() + 1);
  }

  if (toInclusive !== true) {
    toDate.setDate(toDate.getDate() - 1);
  }

  return `${formatDate(fromDate, style)} - ${formatDate(toDate, style)}`;
};

export const getMonthStringFromDate = (date: Date): string => {
  if (date.toString() === "Invalid Date") {
    throw new Error(`Date is invalid`);
  }

  const month = date.getMonth();

  // Not using translation here because we can't use hooks outside components
  switch (month) {
    case 0:
      return "Januar";
    case 1:
      return "Februar";
    case 2:
      return "Mars";
    case 3:
      return "April";
    case 4:
      return "Mai";
    case 5:
      return "Juni";
    case 6:
      return "Juli";
    case 7:
      return "August";
    case 8:
      return "September";
    case 9:
      return "Oktober";
    case 10:
      return "November";
    case 11:
      return "Desember";
    default:
      throw new Error(`Invalid month: ${month}`);
  }
};

export const addDaysToNullableDate = (
  date: Date | null | undefined,
  days: number,
): Date | null => {
  if (date == null) {
    return null;
  }
  return addDaysToDate(date, days);
};

export const addDaysToDate = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const getEndOfMonth = (date: Date): Date => {
  // The '0' for the day parameter sets the date to the last day of the *previous* month.
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
};
